import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { generatePath, useHistory } from "react-router";
import { Upload, CheckCircle, ChevronDown } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import cn from "classnames";
import moment from "moment";
import { TickCircle } from "components/icons/components/TickCircle";
import { ToggleText } from "components/ToggleText";
import "./DesignSelection.css";
import { authorization } from "module/auth";
import { SnackBarContext } from "components/snack-bar";
import { CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import { PossibleFlyerType } from "pages/flyer/builder/drawer";
import { READABLE_CAMPAIGN_INTERNAL_STATUSES } from "pages/constants";
import { CampaignSummaryCard } from "campaign-builder/summary/CampaignSummaryCard";
import { addBusinessDays } from "components/date-picker/utils";
import { DM_MIN_SELECTABLE_DATE } from "../../../constants";

import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import { useStore } from "../../../store";
import { StoreType } from "../../../store/types";
import ROUTES from "../../../config/routing";
import Button from "./Button";
import DesignGallery from "./DesignGallery";
import UploadArea from "./UploadArea";
import useDesignManagement, { DESIGN_STATUS } from "./useDesignManagement";
import { DesignGalleryAddressBoxModal } from "./components";
import { buildAddressBoxSearch, getAddressBoxEntry } from "./utils/builderParams";
import { useFigmaAddressBoxDialog, withDesignEditInfo } from "./utils/useFigmaAddressBoxDialog";
import "../style.css";
import { updateCampaignExtraData, updateCampaign as updateCampaignDB } from "../../../graphQL";
import { Figma } from "static/media/Figma";
import { BrushIcon } from "static/media/BrushIcon";
import { seedImportOrientation } from "pages/creative/pageV2/utils/addressBoxOrientation";

// Utility function to parse timestamp string date
const parseTimestampStringDate = (timestamp: string | number | Date | null | undefined): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "number") {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof timestamp === "string") {
    const trimmed = timestamp.trim();
    if (/^\d+$/.test(trimmed)) {
      const d = new Date(parseInt(trimmed, 10));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const DesignSelection = ({
  onActivateDesign,
  onFormatChange,
  isOpen = true,
  setIsOpen,
  showToggle = false,
  hasDesignSelected = false,
  onFileUpload,
  forceUploadTab = false,
  clearUploadAndTriggerNew = false,
  isNextEnabled: externalIsNextEnabled,
  initialFlyerType,
  deselectSignal,
  formatToRestoreOnDeselect,
}: {
  onActivateDesign?: (design: any) => void;
  onFormatChange?: (format: string) => void;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  showToggle?: boolean;
  hasDesignSelected?: boolean;
  onFileUpload?: (file: any) => void;
  forceUploadTab?: boolean;
  clearUploadAndTriggerNew?: boolean;
  isNextEnabled?: boolean;
  initialFlyerType?: string;
  /** Counter that the parent increments to force the gallery to
   *  deselect its highlighted card. Used by the ADM Design page's
   *  format-change warning when the user picks "Cancel selection".
   *  We track the previous value in a ref so the effect doesn't
   *  fire on mount — only on actual changes. */
  deselectSignal?: number;
  /** Optional `flyerType` to restore on the same deselect signal.
   *  `onDesignSelect` here optimistically writes the picked
   *  design's format to the Zustand campaign store on click; if
   *  the parent decides to cancel that selection (format-mismatch
   *  modal → Cancel) we need to roll the store back to the
   *  pre-click value, otherwise other pages reading from Zustand
   *  would see a half-applied state. */
  formatToRestoreOnDeselect?: string;
}) => {
  const flyerInfo = useGetFlyerInfo();
  const {
    client: { id: clientId },
    campaign: { id: campaignId, campaignName, subtype, flyerType, startDate, internalStatus },
    updateCampaign,
    updateErrors,
    costsCalculationData,
    city,
  }: StoreType = useStore();

  const [activeTab, setActiveTab] = useState<string>("gallery");
  const [activeDesign, setActiveDesign] = useState<any>(null);
  const [lastUploadedDesign, setLastUploadedDesign] = useState<any>(null);
  const lastUploadedDesignRef = useRef<any>(null);
  const [showAddressBoxModal, setShowAddressBoxModal] = useState(false);
  const [pendingDesignId, setPendingDesignId] = useState<string | null>(null);
  const [pendingDesignData, setPendingDesignData] = useState<any>(null); // Store design data when modal opens
  const [launchDateError, setLaunchDateError] = useState<string | null>(null);
  const user = authorization.getUser();
  const runSnackBar = useContext(SnackBarContext);

  // EDDM timezone - default to US Eastern
  const timezone = "America/New_York";

  useEffect(() => {
    // Only sync activeDesign from flyerInfo on initial load or when explicitly needed
    // Don't overwrite if we just activated a design (activeDesign already set)
    if (flyerInfo.flyerInfo?.activeRoomId !== activeDesign?.id && !flyerInfo.isLoading) {
      // Only update if activeDesign is not set (initial load)
      // or if user navigated back and needs to sync
      if (!activeDesign || !activeDesign.id) {
        if (flyerInfo.flyerInfo?.activeRoomId) {
          setActiveDesign({ id: flyerInfo.flyerInfo?.activeRoomId, format: flyerType });
        } else {
          setActiveDesign(null);
        }
      }
      // If activeDesign is already set, keep it (user just activated a design)
      // The background refresh of flyerInfo might have stale data
    }
  }, [flyerInfo.flyerInfo?.activeRoomId, flyerInfo.isLoading]);

  // Handle force upload tab from parent
  useEffect(() => {
    if (forceUploadTab) {
      setActiveTab("upload");
    }
  }, [forceUploadTab]);

  // Parent-driven deselect (used by ADM's format-change warning →
  // Cancel selection). We track the previous signal in a ref so the
  // first render — when both the prop and the ref start equal — is
  // a no-op. Once the parent increments, the diff fires the effect:
  // clear the highlighted card AND restore the Zustand `flyerType`
  // to whatever it was before the optimistic write in
  // `onDesignSelect`.
  const prevDeselectSignalRef = useRef<number | undefined>(deselectSignal);
  useEffect(() => {
    if (prevDeselectSignalRef.current === deselectSignal) return;
    prevDeselectSignalRef.current = deselectSignal;
    setActiveDesign(null);
    if (formatToRestoreOnDeselect !== undefined) {
      updateCampaign({ flyerType: formatToRestoreOnDeselect as PossibleFlyerType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `updateCampaign` is from a stable store ref
  }, [deselectSignal, formatToRestoreOnDeselect]);

  // Handle clear upload and trigger new from parent
  useEffect(() => {
    if (clearUploadAndTriggerNew) {
      setActiveTab("upload");
      // The UploadArea will handle clearing and triggering new upload
    }
  }, [clearUploadAndTriggerNew]);

  const { activateDesign, createDesign, updateDesign } = useDesignManagement({ clientId: clientId as string });
  const history = useHistory();

  useEffect(() => {
    window.parent.postMessage(
      {
        type: "onDesignChange",
        data: {
          designId: activeDesign?.id,
        },
      },
      "*"
    );
  }, [activeDesign]);

  const onRemoveFile = () => {
    const designIdToArchive = lastUploadedDesign?.id || lastUploadedDesignRef.current?.id;
    setActiveDesign(null);
    setLastUploadedDesign(null);
    lastUploadedDesignRef.current = null;

    onActivateDesign?.(null);

    if (designIdToArchive) {
      updateDesign({
        designId: designIdToArchive,
        design: {
          status: DESIGN_STATUS.ARCHIVED,
        },
      });
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleCreateWithFlyerBuilder = async () => {
    try {
      // Validate required data
      if (!user?.id) {
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Authentication required. Please refresh and try again.",
        });
        return;
      }

      if (!campaignId || !clientId) {
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Campaign information missing. Please refresh and try again.",
        });
        return;
      }

      runSnackBar({
        type: "info",
        vertical: "top",
        msg: "Opening design tool...",
      });

      // Ensure authentication token is valid
      const token = localStorage.getItem("apiKey");
      if (!token) {
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Authentication expired. Please log in again.",
        });
        // Force page refresh to trigger re-authentication
        window.location.reload();
        return;
      }

      // Create design with proper error handling
      let design;
      try {
        design = await createDesign({
          filename: `${campaignName || "New"} design`,
          s3Link: "",
          s3CmykLink: "",
          isUploaded: false,
          format: flyerType,
          authorId: user.id,
        });

        if (!design || !design.id) {
          throw new Error("Failed to create design - no ID returned");
        }
      } catch (error) {
        console.error("Error creating design:", error);
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Failed to create design. Please try again.",
        });
        return;
      }

      // Update campaign data
      try {
        // Clear flyerType so the empty state shows in the builder
        updateCampaign({ flyerType: "" as PossibleFlyerType });

        // Activate the design and get the activeRoomId directly
        const activateResult = await activateDesign(campaignId, design.id);

        // Update the local state immediately with the response
        if (activateResult?.activeRoomId) {
          setActiveDesign({
            id: activateResult.activeRoomId.toString(),
            format: flyerType,
          });
        }

        // Refresh the flyer data in background (without blocking the UI)
        Promise.resolve(flyerInfo.getFlyerInfo()).catch((err: any) => {
          console.warn("Background flyer info refresh failed:", err);
        });
      } catch (error) {
        console.error("Error activating design:", error);
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Design created but failed to activate. Please try again.",
        });
        return;
      }

      // Prepare navigation with proper error handling
      try {
        // Navigate without format to trigger the empty state
        const flyerBuilderPath = generatePath(ROUTES.FLYER_BUILDER, {
          clientId,
          campaignId,
        });

        // Add design ID and newDesign flag as query parameters
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set("designId", design.id);
        searchParams.set("newDesign", "true");
        const fullPath = `${flyerBuilderPath}?${searchParams.toString()}`;

        // Use a small delay to ensure all state updates have completed
        setTimeout(() => {
          history.push(fullPath);
        }, 100);

        runSnackBar({
          type: "success",
          vertical: "top",
          msg: "Redirecting to design tool...",
        });
      } catch (error) {
        console.error("Error navigating to builder:", error);
        runSnackBar({
          type: "error",
          vertical: "top",
          msg: "Failed to open design tool. Please try again.",
        });
      }
    } catch (error) {
      console.error("Unexpected error in handleCreateWithFlyerBuilder:", error);
      runSnackBar({
        type: "error",
        vertical: "top",
        msg: "An unexpected error occurred. Please refresh and try again.",
      });
    }
  };

  const onDesignSelect = (design: any) => {
    updateCampaign({ flyerType: design.format });
    if (design.id === activeDesign?.id) {
      setActiveDesign(null);
      onActivateDesign?.(null);
      return;
    }
    setActiveDesign(design);
    onActivateDesign?.(design);
  };

  const onContinueToFlyerDetailClick = async () => {
    // Validate addressBox for DM and ADM campaigns
    const isDMOrADM = subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM || subtype === CAMPAIGN_SUBTYPES_ENUM.ADM;

    // For ADM/EDDM campaigns, all designs must have an addressBox
    // This includes both Flyer Builder designs and uploaded PDFs
    // PDFs will be imported to the canvas automatically, then addressBox can be added

    if (isDMOrADM && activeDesign && !activeDesign.addressBox) {
      // Show modal for both Builder designs and uploaded PDFs
      // Save the design data NOW before it can be overwritten by useEffect
      setPendingDesignId(activeDesign.id);
      setPendingDesignData({ ...activeDesign }); // Store a copy of the design data
      setShowAddressBoxModal(true);
      return;
    }

    const [activateResult] = await Promise.all([
      activateDesign(campaignId, activeDesign.id),
      updateCampaignExtraData({ campaignId, lastActiveStep: "design" }),
    ]);
    updateCampaign({ lastActiveStep: "design" });

    // Update the local state immediately with the response
    if (activateResult?.activeRoomId) {
      setActiveDesign({
        ...activeDesign,
        id: activateResult.activeRoomId.toString(),
      });
    }

    // Refresh the flyer data in background (without waiting)
    Promise.resolve(flyerInfo.getFlyerInfo()).catch((err: any) => {
      console.warn("Background flyer info refresh failed:", err);
    });

    history.push(generatePath(ROUTES.CAMPAIGN_BUILDER, { campaignId, clientId }));
  };

  // Activates the design the builder will open, then opens it with the address box added.
  const activateAndOpenAddressBox = async (designId: string, designData: any, fileLink: string | null) => {
    try {
      // Activate the design BEFORE navigating to the builder
      const activateResult = await activateDesign(campaignId, designId);

      // Use the activeRoomId from the response, not the requested id
      const actualDesignId = activateResult?.activeRoomId?.toString() || designId;

      // Update the local state immediately with the response
      if (activateResult?.activeRoomId) {
        setActiveDesign({
          ...designData,
          id: actualDesignId,
        });
      }

      // Refresh the flyer data in background
      Promise.resolve(flyerInfo.getFlyerInfo()).catch((err: any) => {
        console.warn("Background flyer info refresh failed:", err);
      });

      runSnackBar({
        type: "info",
        vertical: "top",
        msg: "Opening design tool to add address box...",
      });

      // Navigate to Flyer Builder with the design
      const flyerBuilderPath = generatePath(ROUTES.FLYER_BUILDER, {
        clientId,
        campaignId,
        format: designData.format,
      });

      // An uploaded PDF not yet edited in the builder, or a prepared Figma rendition, is imported automatically
      const orientation = await seedImportOrientation(fileLink, actualDesignId, `${campaignId}`);
      const fullPath = `${flyerBuilderPath}${buildAddressBoxSearch(actualDesignId, fileLink, orientation)}`;

      setTimeout(() => {
        history.push(fullPath);
      }, 100);
    } catch (error) {
      console.error(`[AddAddressBox] Error activating or navigating for design ${designId}:`, error);
      runSnackBar({
        type: "error",
        vertical: "top",
        msg: "Failed to activate design. Please try again.",
      });
    }
  };

  // The copy (or flattened original) is what this campaign uses from now on, so it is the one activated.
  const { openFigmaAddressBoxDialog, figmaAddressBoxDialog } = useFigmaAddressBoxDialog({
    onPrepared: (prepared, design) => {
      const isCopy = prepared.designId !== design.id;
      const designData = isCopy
        ? {
            ...design,
            id: prepared.designId,
            designName: prepared.filename,
            designSource: "upload",
            isUploaded: true,
            s3Link: prepared.fileLink,
            hasHtmlContent: false,
            usedByCampaign: true,
          }
        : design;
      void activateAndOpenAddressBox(prepared.designId, designData, prepared.fileLink);
    },
  });

  const handleAddAddressBoxToDesign = async () => {
    setShowAddressBoxModal(false);

    // Use pendingDesignData (saved when modal opened) instead of activeDesign
    // This prevents issues when activeDesign gets overwritten by useEffect
    if (!pendingDesignId || !pendingDesignData) {
      console.error("[AddAddressBox] Missing pendingDesignId or pendingDesignData");
      runSnackBar({
        type: "error",
        vertical: "top",
        msg: "Failed to load design data. Please try again.",
      });
      return;
    }

    const designId = pendingDesignId;
    const designData = await withDesignEditInfo({ ...pendingDesignData, id: designId });
    setPendingDesignId(null);
    setPendingDesignData(null);

    const entry = getAddressBoxEntry(designData);

    // A Figma design is activated only after the copy or flatten choice, so Cancel leaves the campaign untouched
    if (entry.kind === "needsFigmaChoice") {
      void openFigmaAddressBoxDialog(designData);
      return;
    }

    // Validate that uploaded PDFs have valid s3Link
    if (entry.kind === "missingPdf") {
      runSnackBar({
        type: "error",
        vertical: "top",
        msg: "PDF file not found. Please re-upload the design.",
      });
      return;
    }

    await activateAndOpenAddressBox(designId, designData, entry.fileLink);
  };

  const handleCloseAddressBoxModal = () => {
    setShowAddressBoxModal(false);
    setPendingDesignId(null);
    setPendingDesignData(null);
  };

  const onDesignLaterClick = () => {
    updateErrors({ notExistDesignFile: true });
    updateCampaign({ lastActiveStep: "design" });
    updateCampaignExtraData({ campaignId, lastActiveStep: "design" });

    history.push(generatePath(ROUTES.CAMPAIGN_BUILDER, { campaignId, clientId }));
  };
  const isAdm = subtype === CAMPAIGN_SUBTYPES_ENUM.ADM;
  const isEddm = subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM;

  // EDDM date change handler
  const handleStartDateChange = useCallback(
    async (date: Date | null) => {
      if (!date) {
        setLaunchDateError("Please select a date");
        return;
      }

      // Validate the date
      const minDate = addBusinessDays(new Date(), DM_MIN_SELECTABLE_DATE, timezone);
      const newMomentDate = moment(date).format("DD/MM/YYYY");
      const minMomentDate = moment(minDate).format("DD/MM/YYYY");
      const daysDiff = moment(newMomentDate, "DD/MM/YYYY", true).diff(
        moment(minMomentDate, "DD/MM/YYYY", true),
        "days"
      );

      if (daysDiff < 0) {
        setLaunchDateError("Select a different date");
        return;
      }

      setLaunchDateError(null);
      // Create date at midnight in local timezone, then convert to UTC
      // This preserves the selected date regardless of timezone
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const localMidnight = new Date(year, month, day, 0, 0, 0, 0);
      const convertedDate = localMidnight.toISOString();

      // Update campaign with new start date
      updateCampaign({ startDate: convertedDate });

      // Update both Campaign table and CampaignExtraData table
      await Promise.all([
        updateCampaignDB({
          campaignId,
          startDate: new Date(convertedDate),
        }),
        updateCampaignExtraData({
          campaignId,
          startDate: new Date(convertedDate),
        }),
      ]);
    },
    [campaignId, timezone, updateCampaign]
  );

  // Calculate flyer quote data
  const flyerQuote = costsCalculationData?.detailedCost
    ? {
        flyersAmount: costsCalculationData?.flyersAmount || 0,
        discounts: [], // No discounts available in this context
        totalCost: costsCalculationData.detailedCost.totalCost || 0,
      }
    : null;

  // Create targeting data from city addresses
  const targeting = (() => {
    if (!city?.addresses) return [];

    // Count occurrences of each name to add indices
    const nameCounts: { [key: string]: number } = {};

    return city.addresses.map((address) => {
      const baseName = address.value;
      nameCounts[baseName] = (nameCounts[baseName] || 0) + 1;

      // Add index if name repeats
      const displayName = nameCounts[baseName] > 0 ? `${baseName} ${nameCounts[baseName]}` : baseName;

      return {
        id: parseInt(address.id),
        name: displayName,
        csv_url: "",
        client_id: parseInt(clientId),
        metadata: {
          fileUrl: "",
          headers: [],
          firstRow: [],
          headersMap: {},
          rowsAmount: 0,
        },
      };
    });
  })();

  // Button text mapping
  const BUTTON_TITLE_MAP = {
    design: "Continue to Details",
    details: "Continue to Checkout",
  };

  const currentStep = "design";
  const isNextEnabled =
    externalIsNextEnabled !== undefined ? externalIsNextEnabled : !!(activeDesign && activeDesign.id);
  const isLoading = false;
  const isCampaignLoading = false;
  const isReadOnly = false;
  const isCampaignDetailsLoading = false;

  // Mock handlers - these would need to be implemented based on actual requirements
  const handleSeeOrderDetailsModal = () => {
    // Implementation needed
  };

  const onEditFlyerFormat = () => {
    // Implementation needed
  };

  const _onFormatChange = (format: string) => {
    updateCampaign({ flyerType: format as PossibleFlyerType });
    onFormatChange?.(format);
  };

  // For ADM/EDDM, render without the flex wrapper since they use Grid layout
  const shouldUseFlexLayout = !isAdm && !isEddm;

  return (
    <div className={cn({ "flex flex-row gap-4": shouldUseFlexLayout })} data-copilot-step="EDDM Design">
      <div
        className={cn(
          "bg-white rounded-lg shadow-[0px_2px_6px_0px_rgba(39,55,84,0.08)] border border-gray-100 w-full dark:bg-card dark:border-border dark:shadow-none",
          showToggle ? "p-6" : "p-8",
          // Only apply max-width and centering for non-Grid layouts
          { "max-w-5xl mx-auto": shouldUseFlexLayout }
        )}
      >
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <div className="space-y-0.5 max-w-lg">
                <h2
                  className="text-blue-deep-dark dark:text-foreground text-lg font-semibold leading-tight"
                  data-cypress-id="uploadDesignBlockTitle"
                >
                  Design selection
                </h2>
              </div>
              {showToggle && hasDesignSelected && <TickCircle />}
            </div>
            {showToggle && setIsOpen && (
              <div
                className="seeMoreToggle"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
                style={{ cursor: "pointer", zIndex: 10 }}
                data-cypress-id={"seeMoreLessToggle"}
              >
                <ToggleText>See {isOpen ? "less" : "more"}</ToggleText>
                <ChevronDown className={cn("toggleIcon h-5 w-5", isOpen && "toggleIconOpen")} />
              </div>
            )}
          </div>

          <div
            className={cn(
              "transition-all duration-300 ease-in-out",
              !isOpen && showToggle && "max-h-0 overflow-hidden opacity-0",
              isOpen && "max-h-[2000px] opacity-100"
            )}
          >
            <div className="flex flex-col gap-5" style={{ paddingTop: showToggle ? "20px" : "0" }}>
              {isOpen && (
                <p
                  className="text-blue-deep-dark dark:text-foreground text-base font-normal leading-snug"
                  data-cypress-id="uploadDesignBlockDescription"
                >
                  Choose the design for this campaign by selecting one of the options.
                </p>
              )}
              <div className="flex items-center gap-2">
                <div className="flex border border-light-blue dark:border-border rounded-lg p-1">
                  <button
                    onClick={() => handleTabChange("gallery")}
                    className={cn(
                      "flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out min-w-[140px] justify-center will-change-[background-color,color]",
                      activeTab === "gallery"
                        ? "bg-secondary-deep-blue text-white"
                        : "text-secondary-deep-blue dark:text-foreground hover:bg-white dark:hover:bg-accent hover:shadow-md hover:scale-[1.01]"
                    )}
                    data-cypress-id="uploadDesignBlockSelectFromGalleryButton"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Select from gallery
                  </button>
                  <div className="w-px h-4 bg-light-blue dark:bg-border opacity-80 self-center" />
                  {/* Upload Tab */}
                  <button
                    onClick={() => handleTabChange("upload")}
                    className={cn(
                      "flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out min-w-[140px] justify-center will-change-[transform,box-shadow,background-color,color]",
                      activeTab === "upload"
                        ? "bg-secondary-deep-blue text-white"
                        : "text-secondary-deep-blue dark:text-foreground hover:bg-white dark:hover:bg-accent hover:shadow-md hover:scale-[1.01]"
                    )}
                    data-cypress-id="uploadDesignBlockUploadNewButton"
                  >
                    <Upload className="w-4 h-4" />
                    Upload new
                  </button>
                </div>
                <span className="text-grey-medium text-base flex-1 text-center">or</span>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      className="inline-flex items-center justify-center gap-2 border-2 border-light-blue dark:border-border bg-white dark:bg-card text-secondary-deep-blue dark:text-foreground font-medium transition-all duration-200 ease-in-out min-w-[140px] h-[48px] will-change-[transform,box-shadow,background-color] hover:bg-gray-50 dark:hover:bg-accent hover:shadow-md rounded-md text-sm data-[state=open]:bg-neutral-20 dark:data-[state=open]:bg-accent"
                      data-cypress-id="uploadDesignBlockCreateDesignDropdown"
                    >
                      Create new
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content
                    className="min-w-[200px] bg-white dark:bg-popover rounded-sm shadow-lg border border-light-blue-2 dark:border-border p-1 z-50"
                    sideOffset={5}
                    align="end"
                  >
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-3 py-2 text-sm text-secondary-deep-blue dark:text-popover-foreground rounded cursor-pointer outline-none hover:bg-light-blue-1 focus:bg-light-blue-1 dark:hover:bg-accent dark:focus:bg-accent"
                      onSelect={handleCreateWithFlyerBuilder}
                      data-cypress-id="createWithDesignToolMenuItem"
                    >
                      <BrushIcon color="currentColor" size={16} />
                      Create with Design Tool
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-3 py-2 text-sm text-secondary-deep-blue dark:text-popover-foreground rounded cursor-pointer outline-none hover:bg-light-blue-1 focus:bg-light-blue-1 dark:hover:bg-accent dark:focus:bg-accent"
                      onSelect={() => {
                        const newWindow = window.open(
                          "https://www.figma.com/community/plugin/1609266944915750859/oppizi-frames-design-and-launch-high-performing-offline-ads",
                          "_blank"
                        );
                        if (newWindow) {
                          newWindow.opener = null;
                        }
                      }}
                      data-cypress-id="createInFigmaMenuItem"
                    >
                      <Figma size={16} />
                      Create in Figma
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </div>

              {/* Content Area */}
              <div className="flex gap-6">
                {/* Main Content */}
                <div className="flex-1 min-w-0 w-full">
                  <div className="min-h-[400px] w-full">
                    {activeTab === "gallery" ? (
                      <DesignGallery activeDesign={activeDesign} onDesignSelect={onDesignSelect} />
                    ) : (
                      <UploadArea
                        onFileUpload={(design: any) => {
                          setLastUploadedDesign(design);
                          lastUploadedDesignRef.current = design;
                          onDesignSelect(design);
                          onFileUpload?.(design);
                        }}
                        onFormatChange={_onFormatChange}
                        maxFileSize={50}
                        acceptedFileTypes={["application/pdf"]}
                        onRemoveFile={onRemoveFile}
                        clearAndTriggerNew={clearUploadAndTriggerNew}
                        _selectedFlyerType={initialFlyerType || flyerType}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cn("flex-1 min-w-[360px] max-w-[420px]", { hidden: isAdm || isEddm })}>
        <CampaignSummaryCard
          campaign={{
            campaignName: campaignName,
            campaignStartDate: parseTimestampStringDate(startDate) ?? undefined,
            format: flyerType || "-",
          }}
          targeting={targeting as any}
          flyerQuote={flyerQuote}
          onClickNext={() => onContinueToFlyerDetailClick()}
          buttonText={BUTTON_TITLE_MAP[currentStep]}
          isNextEnabled={isNextEnabled}
          isLoading={isLoading || isCampaignLoading}
          onClickSecondaryButton={onDesignLaterClick}
          secondaryButtonText="Design later"
          isReadOnly={isReadOnly}
          reviewMeetingDate={undefined}
          internalStatus={
            internalStatus
              ? READABLE_CAMPAIGN_INTERNAL_STATUSES[internalStatus as keyof typeof READABLE_CAMPAIGN_INTERNAL_STATUSES]
              : undefined
          }
          isCampaignDetailsLoading={isCampaignDetailsLoading}
          // EDDM-specific props for date picker
          isEddm={isEddm}
          onStartDateChange={handleStartDateChange}
          launchDateError={launchDateError}
          timezone={timezone}
          isSubmitted={internalStatus === "submitted"}
        />
      </div>

      {/* Address Box Modal */}
      <DesignGalleryAddressBoxModal
        open={showAddressBoxModal}
        onClose={handleCloseAddressBoxModal}
        onAddAddressBox={handleAddAddressBoxToDesign}
      />
      {figmaAddressBoxDialog}
    </div>
  );
};

export default DesignSelection;
